import * as React from "react";
import { Navigate, useNavigate } from "react-router";
import { createWorkspaceSchema, setPasswordSchema } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Field, Input, PasswordInput, Form } from "@/components/ui/Field";
import { FormMessage, Splash, Spinner } from "@/components/ui/Feedback";
import { useAuth } from "@/lib/auth-context";
import { apiErrorMessage } from "@/lib/api";
import { describeAuthError, fieldErrorsFrom } from "@/lib/auth-errors";
import { PLACEHOLDER } from "@/lib/placeholders";

/**
 * Self sign-up, steps three and four: choose a password, then create the company
 * and its first site.
 *
 * The only screen that can resolve `status === "onboarding"`, which is why the
 * guard sends people here rather than to the "account unavailable" dead end. A
 * user arrives with a valid session and no tenancy at all: no company, no site,
 * no role, no capabilities. They can read nothing until the second form is
 * submitted, and every RLS helper returns NULL for them in the meantime.
 *
 * ── Why the password is here and not on /signup ─────────────────────────────
 *
 * Because it needs a session, and /signup has none. The address is proven by the
 * emailed code, that produces a session, and only then is there an identity
 * worth attaching a credential to. Doing it the other way round, which is what
 * this flow used to do, means an abandoned sign-up leaves a working password on
 * an address nobody has shown they can read.
 *
 * Social sign-in skips step three entirely. The provider owns that credential,
 * and asking a Google user to invent a TaskIQ password would create a second way
 * into the account for no benefit.
 *
 * ── No back arrow here, and that is a deliberate exception ──────────────────
 *
 * Every other screen in the auth flow carries one in `AuthLayout`'s title row.
 * These two do not, because there is nowhere honest for it to go: a session
 * already exists, `/signup` re-enters as `WorkspaceStage` for anyone with one
 * (see `RedirectIfAuthed`), and there is no previous *form* to return to - only
 * the identity a person is now signed in as. `SignOutLink` in the footer is the
 * real escape hatch, named for what it does rather than dressed as a "back".
 */

type Stage = "password" | "workspace";

export function Onboarding() {
  const { status, session, needsPassword, createWorkspace, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();

  // Whatever the sign-up form or the OAuth provider supplied, carried through
  // `user_metadata` purely to save re-typing. Not tenancy: the user can rewrite
  // their own metadata, so nothing security-relevant may be read from it.
  const suggestedName = (session?.user.user_metadata?.full_name as string | undefined) ?? "";
  const passwordAlreadySet = session?.user.user_metadata?.password_set === true;

  const [stage, setStage] = React.useState<Stage>(
    needsPassword && !passwordAlreadySet ? "password" : "workspace"
  );

  if (status === "loading") {
    return <Splash label="Loading your account" />;
  }

  if (status === "signed-out") return <Navigate to="/login" replace />;

  // Already has a workspace, including the case where they were invited while
  // half way through signing up and `create_workspace` refused to make a second
  // one. The console routes them into the company that invited them.
  if (status !== "onboarding") return <Navigate to="/app" replace />;

  if (stage === "password") {
    return (
      <PasswordStage
        onSubmit={updatePassword}
        onDone={() => setStage("workspace")}
        onSignOut={signOut}
      />
    );
  }

  return (
    <WorkspaceStage
      suggestedName={suggestedName}
      onSubmit={createWorkspace}
      onDone={() => navigate("/app", { replace: true })}
      onSignOut={signOut}
    />
  );
}

/* ──────────────────────────── Step 3: password ──────────────────────────── */

function PasswordStage({
  onSubmit,
  onDone,
  onSignOut,
}: {
  onSubmit: (password: string) => Promise<void>;
  onDone: () => void;
  onSignOut: () => Promise<void>;
}) {
  const [values, setValues] = React.useState({ password: "", confirmPassword: "" });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = setPasswordSchema.safeParse(values);
    if (!parsed.success) {
      setFieldErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      await onSubmit(parsed.data.password);
      onDone();
    } catch (err) {
      setFormError(describeAuthError(err));
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Choose a password"
      subtitle="Your email address is confirmed. This is how you'll sign in from now on."
      footer={<SignOutLink onSignOut={onSignOut} />}
    >
      <Form onSubmit={submit}>
        <Field
          label="Password"
          htmlFor="password"
          required
          hint="At least 10 characters."
          error={fieldErrors.password}
        >
          <PasswordInput
            id="password"
            name="password"
            inputSize="md"
            autoComplete="new-password"
            autoFocus
            invalid={Boolean(fieldErrors.password)}
            value={values.password}
            onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
          />
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirmPassword"
          required
          error={fieldErrors.confirmPassword}
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            inputSize="md"
            autoComplete="new-password"
            invalid={Boolean(fieldErrors.confirmPassword)}
            value={values.confirmPassword}
            onChange={(e) => setValues((v) => ({ ...v, confirmPassword: e.target.value }))}
          />
        </Field>

        <FormMessage>{formError}</FormMessage>

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          Continue
        </Button>
      </Form>
    </AuthLayout>
  );
}

/* ─────────────────────── Step 4: company and first site ─────────────────────── */

function WorkspaceStage({
  suggestedName,
  onSubmit,
  onDone,
  onSignOut,
}: {
  suggestedName: string;
  onSubmit: (input: {
    company_name: string;
    site_name: string;
    timezone: string;
    full_name?: string;
  }) => Promise<unknown>;
  onDone: () => void;
  onSignOut: () => Promise<void>;
}) {
  const [values, setValues] = React.useState({ company_name: "", site_name: "" });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = createWorkspaceSchema.safeParse({
      ...values,
      // The browser knows the zone. Asking for it would be a dropdown of 400
      // options on one of the first screens a new user ever sees, and it is
      // editable later per site, which is where it actually matters.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      full_name: suggestedName || undefined,
    });
    if (!parsed.success) {
      setFieldErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      await onSubmit(parsed.data);
      onDone();
    } catch (err) {
      setFormError(apiErrorMessage(err, "Could not create your workspace"));
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Set up your workspace"
      subtitle="Two details and you're in. Choose them carefully - neither can be renamed."
      footer={<SignOutLink onSignOut={onSignOut} />}
    >
      <Form onSubmit={submit}>
        <Field
          label="Workspace name"
          htmlFor="company_name"
          required
          hint="The organisation your crews work for. Set once."
          error={fieldErrors.company_name}
        >
          <Input
            id="company_name"
            name="organization"
            inputSize="md"
            autoComplete="organization"
            autoFocus
            placeholder={PLACEHOLDER.companyName}
            invalid={Boolean(fieldErrors.company_name)}
            value={values.company_name}
            onChange={(e) => setValues((v) => ({ ...v, company_name: e.target.value }))}
          />
        </Field>

        <Field
          label="First location"
          htmlFor="site_name"
          required
          // Asked for rather than defaulted to something like "Head office". A
          // placeholder name is one nobody goes back and fixes, and every
          // assessment from then on is filed against it.
          hint="A crew, depot or region. Every assessment is filed against one, and it is set once."
          error={fieldErrors.site_name}
        >
          <Input
            id="site_name"
            name="site_name"
            inputSize="md"
            placeholder={PLACEHOLDER.siteName}
            invalid={Boolean(fieldErrors.site_name)}
            value={values.site_name}
            onChange={(e) => setValues((v) => ({ ...v, site_name: e.target.value }))}
          />
        </Field>

        <FormMessage>{formError}</FormMessage>

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          Create workspace
        </Button>

        <p className="auth-hint-block">
          You'll be the administrator of this company and can invite your team straight away.
        </p>
      </Form>
    </AuthLayout>
  );
}

function SignOutLink({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <button type="button" onClick={() => void onSignOut()} className="auth-alt">
      Sign out
    </button>
  );
}
